import React, { useState, useEffect } from 'react'
import { supabase } from '../supabase'

const MAX_SHOWS_PER_STEP = 3
const GOLD = '#c9a84c'

// Tiers ranked at or above Debt Crusher get Clubhouse access (matches
// forum_rooms: Dreamers/Budding Star are locked out entirely).
const CLUBHOUSE_ELIGIBLE_TIERS = [
  'debt_crusher', 'shining_star', 'achievers_club', 'game_changer', 'wealth_builder',
  'visionaries', 'luminaries', 'trailblazers', 'pioneers', 'tycoons',
]

// Steps fall into two kinds:
//  - Data-checked: `isDone` reads real data already loaded in Dashboard.
//    Completing the real action elsewhere in the app silently advances this
//    step, with no need to click through the popup at all.
//  - Click-to-complete: `completeOnClick: true` for things with no natural
//    "done" signal (informational steps, or pages that are computed/viewed
//    rather than filled in, like Net Worth or Credit Score). Clicking the
//    button itself marks it complete; dismissing (✕) just counts as a show.
const STEPS = [
  {
    key: 'intro_action_button',
    title: 'Welcome to DebtCrush! 👋',
    body: "Quick tip: the checklist icon at the top of your screen is your Action Button — it shows exactly what to do next to reach your club's next milestone, anytime.",
    cta: 'Got it!',
    page: null,
    completeOnClick: true,
  },
  {
    key: 'first_loan',
    title: "Let's get started",
    body: 'Add the loan or debt you want to track — even a rough estimate is fine, you can refine it anytime.',
    cta: 'Add Your First Loan',
    page: 'loans',
    isDone: ({ loans }) => (loans || []).length > 0,
  },
  {
    key: 'expenses',
    title: 'Nice work! 🎉',
    body: 'Track your monthly expenses next — this helps DebtCrush see your full financial picture, not just your debt.',
    cta: 'Add Your Expenses',
    page: 'expenses',
    isDone: ({ expenses }) => (expenses || []).length > 0,
  },
  {
    key: 'budget_planner',
    title: 'Plan ahead',
    body: 'The Budget Planner helps you see where your money is going each month, so you can find more to put toward your debt.',
    cta: 'Open Budget Planner',
    page: 'budget',
    completeOnClick: true,
  },
  {
    key: 'insurance',
    title: 'Protect what you\'re building',
    body: "Add your insurance policies so DebtCrush can track them alongside everything else — and so you don't lose progress to an unexpected event.",
    cta: 'Add Insurance',
    page: 'insurance',
    isDone: ({ insurancePolicies }) => (insurancePolicies || []).length > 0,
  },
  {
    key: 'emergency_fund',
    title: "You're on a roll! 💪",
    body: 'A safety net matters as much as paying off debt. Set a target for your Emergency Fund so you know what you\'re building toward.',
    cta: 'Set Up Emergency Fund',
    page: 'emergency',
    isDone: ({ emergencyFund }) => parseFloat(emergencyFund?.target_amount) > 0,
  },
  {
    key: 'clubhouse_concerns',
    title: 'Welcome to the Clubhouse! 🏆',
    body: "You've unlocked the Clubhouse — a space to share a Concern (a struggle, question, or milestone) and see what others at your level are going through too.",
    cta: 'Share Your First Concern',
    page: 'forum',
    completeOnClick: true,
    // Only relevant once a member's paid tier actually unlocks Clubhouse —
    // stays paused (not shown, not counted as a show) until then.
    gate: ({ membershipTier }) => CLUBHOUSE_ELIGIBLE_TIERS.includes(membershipTier),
  },
  {
    key: 'networth',
    title: 'See the bigger picture',
    body: 'Net Worth pulls everything together — assets, investments, and debt — into one clear number that shows your real progress.',
    cta: 'Check Your Net Worth',
    page: 'networth',
    completeOnClick: true,
  },
  {
    key: 'investments',
    title: 'Start building forward',
    body: 'Once your debt is under control, investments are what turn "debt-free" into "wealth-building." Add any you already have, even small ones.',
    cta: 'Add Investments',
    page: 'investments',
    isDone: ({ investments }) => (investments || []).length > 0,
  },
  {
    key: 'credit_score',
    title: 'One last thing',
    body: 'A healthy credit score opens doors — better loan rates, easier approvals. Check out the tips to keep yours in good shape.',
    cta: 'See Credit Score Tips',
    page: 'credit',
    completeOnClick: true,
  },
]

export default function OnboardingPrompt({ session, loans, expenses, emergencyFund, insurancePolicies, investments, membershipTier, onNavigate }) {
  const userId = session?.user?.id
  const [profile, setProfile] = useState(null)
  const [visible, setVisible] = useState(false)

  const data = { loans, expenses, emergencyFund, insurancePolicies, investments, membershipTier }

  useEffect(() => {
    if (!userId) return
    supabase
      .from('profiles')
      .select('onboarding_step, onboarding_step_shows, onboarding_completed')
      .eq('id', userId)
      .single()
      .then(({ data }) => setProfile(data))
  }, [userId])

  useEffect(() => {
    if (!profile || profile.onboarding_completed) return
    resolveStep(profile)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, loans, expenses, emergencyFund, insurancePolicies, investments, membershipTier])

  async function resolveStep(p) {
    let stepIndex = p.onboarding_step - 1
    let shows = p.onboarding_step_shows

    // Auto-advance past any already-completed data-checked steps.
    while (stepIndex < STEPS.length && STEPS[stepIndex].isDone && STEPS[stepIndex].isDone(data)) {
      stepIndex += 1
      shows = 0
    }

    if (stepIndex >= STEPS.length) {
      await supabase.from('profiles').update({ onboarding_completed: true }).eq('id', userId)
      setVisible(false)
      return
    }

    const step = STEPS[stepIndex]

    // Gated step (Clubhouse) not yet applicable — pause here, don't show,
    // don't burn a "shows" attempt, just wait until they're eligible.
    if (step.gate && !step.gate(data)) {
      if (stepIndex !== p.onboarding_step - 1) {
        await supabase.from('profiles').update({ onboarding_step: stepIndex + 1, onboarding_step_shows: 0 }).eq('id', userId)
      }
      setVisible(false)
      return
    }

    if (shows >= MAX_SHOWS_PER_STEP) {
      if (stepIndex !== p.onboarding_step - 1 || shows !== p.onboarding_step_shows) {
        await supabase.from('profiles').update({ onboarding_step: stepIndex + 1, onboarding_step_shows: shows }).eq('id', userId)
      }
      setVisible(false)
      return
    }

    if (stepIndex !== p.onboarding_step - 1) {
      await supabase.from('profiles').update({ onboarding_step: stepIndex + 1, onboarding_step_shows: 0 }).eq('id', userId)
    }
    setVisible(true)
  }

  async function handlePrimaryClick() {
    const stepIndex = profile.onboarding_step - 1
    const step = STEPS[stepIndex]
    setVisible(false)

    const isLastStep = stepIndex === STEPS.length - 1

    if (step.completeOnClick) {
      // Clicking through is the completion signal for this kind of step —
      // advance immediately rather than just logging another "show".
      if (isLastStep) {
        await supabase.from('profiles').update({ onboarding_completed: true }).eq('id', userId)
      } else {
        await supabase.from('profiles').update({ onboarding_step: stepIndex + 2, onboarding_step_shows: 0 }).eq('id', userId)
      }
    } else {
      // Data-checked step: clicking just navigates them there; real
      // completion is picked up automatically on the next resolve.
      await supabase.from('profiles').update({ onboarding_step_shows: profile.onboarding_step_shows + 1 }).eq('id', userId)
    }

    if (step.page && onNavigate) onNavigate(step.page)
  }

  async function handleDismiss() {
    setVisible(false)
    await supabase.from('profiles').update({ onboarding_step_shows: profile.onboarding_step_shows + 1 }).eq('id', userId)
  }

  if (!visible || !profile) return null

  const step = STEPS[profile.onboarding_step - 1]
  if (!step) return null

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) handleDismiss() }}
      style={{ position: 'fixed', inset: 0, background: 'var(--modal-backdrop)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
    >
      <div style={{ background: '#0d0d0d', border: '1px solid rgba(201,168,76,0.3)', borderRadius: '12px', width: '100%', maxWidth: '400px', padding: '1.75rem', textAlign: 'center', position: 'relative' }}>
        <button
          onClick={handleDismiss}
          style={{ position: 'absolute', top: '0.75rem', right: '0.9rem', background: 'none', border: 'none', color: 'rgba(245,240,232,0.4)', fontSize: '1.1rem', cursor: 'pointer', lineHeight: 1 }}
        >✕</button>

        <div style={{ display: 'flex', justifyContent: 'center', gap: '0.3rem', marginBottom: '1rem', flexWrap: 'wrap', maxWidth: '260px', margin: '0 auto 1rem' }}>
          {STEPS.map((s, i) => (
            <div key={s.key} style={{ width: '6px', height: '6px', borderRadius: '99px', background: i === profile.onboarding_step - 1 ? GOLD : 'rgba(245,240,232,0.2)' }} />
          ))}
        </div>

        <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.15rem', fontWeight: '700', color: '#f5f0e8', marginBottom: '0.6rem' }}>
          {step.title}
        </div>
        <div style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.85rem', color: 'rgba(245,240,232,0.7)', lineHeight: 1.6, marginBottom: '1.4rem' }}>
          {step.body}
        </div>

        <button
          onClick={handlePrimaryClick}
          style={{ background: GOLD, color: '#0d0d0d', border: 'none', padding: '0.65rem 1.5rem', borderRadius: '6px', fontFamily: 'monospace', fontSize: '0.74rem', fontWeight: '700', cursor: 'pointer', letterSpacing: '0.03em', width: '100%' }}
        >
          {step.cta}
        </button>
      </div>
    </div>
  )
}
